import type {
    UploadPhotoPictureTypeIdEnum,
    UploadPhotoResponse,
    UploadPhotoResponseErrorTypeEnum,
} from "libs/codegen/property-onboarding";
import { useCallback, useState } from "react";
import { toOnboardingApiIdentity } from "../../routes/onboardingRouteApi";
import { useOnboardingRouteId } from "../../useOnboardingRouteId";
import type { PhotoUploadProps } from "./PhotoUpload";

export type UploadQueue = Record<string, PhotoUploadProps>;

interface Options {
    onSuccess: (photoId: number, onboardingPhotoId: string | null | undefined, src: string) => void;
    onError: (error?: UploadPhotoResponseErrorTypeEnum) => void;
    pictureTypeId: UploadPhotoPictureTypeIdEnum;
    roomId?: string;
}

export const useUploadPhotoHandler = ({ onSuccess, onError, pictureTypeId, roomId }: Options) => {
    const route = useOnboardingRouteId();
    const apiIdentity = toOnboardingApiIdentity(route);
    const [uploadQueue, setUploadQueue] = useState<UploadQueue>({});

    const upsertStatus = (key: string, progress: number, src?: string) => {
        setUploadQueue((prev) => {
            let updatedQueue = { ...prev };

            if (progress === -1) delete updatedQueue[key];
            else
                updatedQueue[key] = {
                    src: src!,
                    percentage: progress,
                };

            return updatedQueue;
        });
    };

    const upload = useCallback(
        (file: File) => {
            const blobString = URL.createObjectURL(file);
            upsertStatus(blobString, 0, blobString);

            const uploadPath = `/${encodeURIComponent(route.lang)}/list-my-property/api/one-onboarding/uploadPhoto`;
            const formData = new FormData();
            formData.append("file", file);

            const queryParams = new URLSearchParams();
            if (apiIdentity.propertyId !== undefined) {
                queryParams.append("propertyId", String(apiIdentity.propertyId));
            }
            if (apiIdentity.onboardingId !== undefined) {
                queryParams.append("onboardingId", apiIdentity.onboardingId);
            }
            queryParams.append("pictureTypeId", pictureTypeId.toString());

            if (roomId) queryParams.append("roomId", roomId);

            const xhr = new XMLHttpRequest();

            xhr.upload.addEventListener("progress", (event) => {
                if (event.lengthComputable)
                    upsertStatus(blobString, Math.round((event.loaded * 100) / event.total), blobString);
            });

            xhr.addEventListener("load", () => {
                try {
                    const data = JSON.parse(xhr.responseText) as UploadPhotoResponse;

                    if (xhr.status >= 200 && xhr.status < 300) {
                        if (data.errorType) {
                            onError(data.errorType);
                        } else {
                            onSuccess(data.id, data.onboardingPhotoId, blobString);
                        }
                    } else {
                        onError(data?.errorType as UploadPhotoResponseErrorTypeEnum | undefined);
                    }
                } catch {
                    onError(undefined);
                }
            });

            xhr.addEventListener("error", () => {
                onError(undefined);
            });

            xhr.addEventListener("loadend", () => {
                upsertStatus(blobString, -1);
            });

            xhr.open("POST", uploadPath + "?" + queryParams.toString(), true);
            xhr.send(formData);
        },
        [upsertStatus, route.lang, apiIdentity.propertyId, apiIdentity.onboardingId, pictureTypeId, roomId],
    );

    return {
        queue: uploadQueue,
        upload,
    };
};
